import { AWS_INSTANCE_TYPES } from "./aws_instance_types";
import { AWS_REGIONS } from "../../../../shared/aws-regions";

export type JobConfigFieldType = 'number' | 'text' | 'select' | 'boolean';

export interface JobConfigField {
  key: string;
  label: string;
  type: JobConfigFieldType;
  /** Field must have a value before the job can be submitted. */
  required?: boolean;
  /** Field is not shown in the main form UI. */
  hidden?: boolean;
  /** Value is sourced from the job config API (may apply to shown or hidden fields). */
  inferred?: boolean;
  /** Hard-coded initial form value; field is never patched from the API. */
  default?: any;
  placeholder?: string;
  unit?: string;
  tooltip?: string;
  options?: { value: string | number | boolean; label: string }[];
}

export const JOB_CONFIG_FIELDS: JobConfigField[] = [
  // ── Identity ──────────────────────────────────────────────────────────────
  {
    key: 'job_name',
    label: 'Job Name',
    type: 'text',
    hidden: true,
    placeholder: 'e.g. FWI_Run_001',
    tooltip: 'Unique identifier for this job. Also used to derive the job location path.',
  },
  {
    key: 'region',
    label: 'AWS Region',
    type: 'select',
    hidden: true,
    inferred: true,
    required: true,
    options: AWS_REGIONS,
  },
  {
    key: 's3_bucket',
    label: 'S3 Bucket',
    type: 'text',
    required: true,
    inferred: true,
    hidden: true,
    placeholder: 'e.g. my-seismic-bucket',
  },
  {
    key: 'xwi_prefix',
    label: 'XWI Prefix',
    type: 'text',
    hidden: true,
    inferred: true,
  },

  // ── XWI Config ────────────────────────────────────────────────────────────
  {
    key: 'xwi_runfile',
    label: 'XWI Runfile',
    type: 'text',
    hidden: true,
  },
  {
    key: 'xwi_starting_checkpoint',
    label: 'Starting Checkpoint',
    type: 'number',
    hidden: true,
    default: 0,           // fixed default; never patched from the API
  },
  // {
  //   key: 'debug_instances',
  //   label: 'Debug Instances',
  //   type: 'boolean',
  //   hidden: true,
  //   inferred: true,
  // },

  // ── Compute ───────────────────────────────────────────────────────────────
  {
    key: 'number_of_workers',
    label: 'Number of Workers',
    type: 'number',
    required: true,
    inferred: true,
    placeholder: '8',
    tooltip: 'Number of worker instances that run the computation in parallel. More workers speeds up the job but increases cost.',
  },
  {
    key: 'scheduler_instance_type',
    label: 'Scheduler Instance Type',
    type: 'select',
    options: AWS_INSTANCE_TYPES,
    required: true,
    inferred: true,
    placeholder: 'e.g. tiny or c5.4xlarge',
    tooltip: 'EC2 instance type for the scheduler node, which coordinates work distribution across workers. Pick a specific type, or an instance family to let the infrastructure choose a matching instance.',
  },
  {
    key: 'worker_instance_type',
    label: 'Worker Instance Type',
    type: 'select',
    options: AWS_INSTANCE_TYPES,
    required: true,
    inferred: true,
    placeholder: 'e.g. huge or c5.18xlarge',
    tooltip: 'EC2 instance type for each worker node. Workers perform the actual seismic computation. Pick a specific type, or an instance family to let the infrastructure choose a matching instance.',
  },
  {
    key: 'scheduler_disksize_GB',
    label: 'Scheduler Disk Size',
    type: 'number',
    unit: 'GB',
    // required: true,
    // inferred: true,
    default: 0,
    hidden: true,
    placeholder: '100',
  },
  {
    key: 'worker_disksize_GB',
    label: 'Worker Disk Size',
    type: 'number',
    unit: 'GB',
    // required: true,
    // inferred: true,
    default: 0,
    hidden: true,
    placeholder: '500',
  },

  // ── Paths ─────────────────────────────────────────────────────────────────
  {
    key: 'model_path',
    label: 'Model Path',
    type: 'text',
    required: true,
    placeholder: '/bucket/project/input/model',
    tooltip: 'Path to the starting velocity model file(s).',
  },
  {
    key: 'input_path',
    label: 'Input Path',
    type: 'text',
    inferred: true,
    placeholder: '/bucket/project/input',
    tooltip: 'Root path for all input data. Sets the default for model, geometry, wavelets and traces paths.',
  },
  {
    key: 'geometry_path',
    label: 'Geometry Path',
    type: 'text',
    required: true,
    inferred: true,
    placeholder: '/bucket/project/input/geometry',
    tooltip: 'Path to the acquisition geometry data describing source and receiver positions.',
  },
  {
    key: 'wavelets_path',
    label: 'Wavelets Path',
    type: 'text',
    required: true,
    inferred: true,
    placeholder: '/bucket/project/input/wavelets',
    tooltip: 'Path to the source wavelet file(s) used in forward modelling.',
  },
  {
    key: 'traces_path',
    label: 'Traces Path',
    type: 'text',
    required: true,
    inferred: true,
    placeholder: '/bucket/project/input/traces',
    tooltip: 'Path to the observed seismic trace data used as the inversion target.',
  },
  {
    key: 'output_path',
    label: 'Output Path',
    type: 'text',
    // inferred: true,
    placeholder: '/bucket/project/output',
    tooltip: 'Root path for all output data. Sets the default for checkpoint and dumps paths.',
  },
  {
    key: 'checkpoint_path',
    label: 'Checkpoint Path',
    type: 'text',
    required: true,
    inferred: true,
    placeholder: '/bucket/project/output/checkpoints',
    tooltip: 'Path where job checkpoints are saved, allowing interrupted runs to be resumed.',
  },
  {
    key: 'dumps_path',
    label: 'Dumps Path',
    type: 'text',
    required: true,
    inferred: true,
    placeholder: '/bucket/project/output/dumps',
    tooltip: 'Path where intermediate model dumps are written during the inversion.',
  },

  // ── Scripts ───────────────────────────────────────────────────────────────
  {
    key: 'scheduler_pre_xwi_script',
    label: 'Scheduler Pre-XWI Script',
    type: 'text',
    inferred: true,
    placeholder: '/scripts/pre_scheduler.sh',
    tooltip: 'Optional script executed on the scheduler node before XWI starts.',
  },
  {
    key: 'worker_pre_xwi_script',
    label: 'Worker Pre-XWI Script',
    type: 'text',
    inferred: true,
    placeholder: '/scripts/pre_worker.sh',
    tooltip: 'Optional script executed on each worker node before XWI starts.',
  },
  {
    key: 'scheduler_post_xwi_script',
    label: 'Scheduler Post-XWI Script',
    type: 'text',
    inferred: true,
    placeholder: '/scripts/post_scheduler.sh',
    tooltip: 'Optional script executed on the scheduler node after XWI completes.',
  },
  {
    key: 'worker_post_xwi_script',
    label: 'Worker Post-XWI Script',
    type: 'text',
    inferred: true,
    placeholder: '/scripts/post_worker.sh',
    tooltip: 'Optional script executed on each worker node after XWI completes.',
  },
  {
    key: 'epicmods_path',
    label: 'Epicmods Path',
    type: 'text',
    inferred: true,
    placeholder: '/bucket/project/epicmods',
    tooltip: 'Path to the EpicMods parameter file used for model perturbations during inversion.',
  },

  // ── Hidden / API-only ─────────────────────────────────────────────────────
  {
    key: 'xwi_exec_path',
    label: 'XWI Exec Path',
    type: 'text',
    required: true,
    inferred: true,
    placeholder: '/opt/xwi/bin/xwi',
    tooltip: 'Absolute path to the XWI executable on the worker instances.',
  },
  {
    key: 'environment_variables',
    label: 'Environment Variables',
    type: 'text',
    hidden: true,
    default: "env.sh"
    // required: true,
    // inferred: true,
  },
];

export const JOB_CONFIG_GROUPS: { parentKey: string; childKeys?: string[] }[] = [
  { parentKey: 'model_path', childKeys: [] },
  { parentKey: 'output_path', childKeys: ['checkpoint_path', 'dumps_path'] },
  { parentKey: 'input_path',  childKeys: ['geometry_path', 'wavelets_path', 'traces_path'] },
];

export interface DerivedFieldRule {
  targetKey: string;
  /** Form field keys whose changes trigger recomputation. */
  formDeps?: string[];
  /** Whether this field also depends on the parent's jobPath input. */
  jobPathDep?: boolean;
  /** Returns the derived value, or null to leave the field unchanged. */
  compute: (formValues: Record<string, any>, jobPath: string) => any;
}

export interface FieldSection {
  /** Heading shown in the collapse panel. */
  label: string;
  /** Keys of the fields rendered inside this section. Order is preserved. */
  fieldKeys: string[];
}

export const JOB_CONFIG_SECTIONS: FieldSection[] = [
  {
    label: 'Scripts',
    fieldKeys: [
      'scheduler_pre_xwi_script',
      'worker_pre_xwi_script',
      'scheduler_post_xwi_script',
      'worker_post_xwi_script',
      'xwi_exec_path',
      'epicmods_path'
    ],
  },
];

export const JOB_CONFIG_DERIVED: DerivedFieldRule[] = [
  {
    targetKey: 'xwi_runfile',
    formDeps: ['xwi_prefix'],
    jobPathDep: false,
    compute: (vals, _) => {
      const prefix = vals.xwi_prefix;
      return prefix ? `${prefix}-Runfile.key` : null;
    },
  },
  {
    targetKey: 'output_path',
    jobPathDep: true,
    compute: (_, jobPath) => {
      if (!jobPath) return null;
      // Strip s3:// scheme then remove the bucket segment (everything up to and including the first /)
      const withoutScheme = jobPath.replace(/^s3:\/\//, '');
      const slashIdx = withoutScheme.indexOf('/');
      return slashIdx >= 0 ? withoutScheme.substring(slashIdx + 1) : withoutScheme;
    },
  },
  {
    targetKey: 'model_path',
    jobPathDep: true,
    compute: (_, jobPath) => {
      if (!jobPath) return null;
      // Strip s3:// scheme then remove the bucket segment (everything up to and including the first /)
      const withoutScheme = jobPath.replace(/^s3:\/\//, '');
      const slashIdx = withoutScheme.indexOf('/');
      return slashIdx >= 0 ? withoutScheme.substring(slashIdx + 1) : withoutScheme;
    },
  },
  // {
  //   targetKey: 'environment_variables',
  //   jobPathDep: true,
  //   compute: (_, jobPath) => (jobPath ? `${jobPath}/env.sh` : null),
  // },
];